// src/pages/Dashboard/DashboardGlobal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Alert, Badge } from 'react-bootstrap';
import TopHeader from '../../common/Header/TopHeader';
import NavigationTabs from '../components/layout/NavigationTabs';
import KPICard from '../components/charts/KPICard';
import VisitantesChart from '../components/charts/VisitantesChart';
import BreakEvenChart from '../components/charts/BreakEvenChart';
import DistribucionIngresosChart from '../components/charts/DistribucionIngresosChart';
import InvertidoVsEbitdaChart from '../components/charts/InvertidoVsEbitdaChart';
import ApalancamientoChart from '../components/charts/ApalancamientoChart';
import { DeudaPorAreaChart, ActivosPorAreaChart } from '../components/charts/DeudaActivosCharts';
import RetrasosImpagosCharts from '../components/charts/RetrasosImpagosCharts';
import CostesCharts from '../components/charts/CostesCharts';
import MerchandisingCharts from '../components/charts/MerchandisingCharts';
import FoodBeverageCharts from '../components/charts/FoodBeverageCharts';
import CRMFunnelCharts from '../components/charts/CRMFunnelCharts';
import TicketingCharts from '../components/charts/TicketingCharts';
import kpiService from '../services/kpiService';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';
import FilterDashGlobal from './FilterDashGlobal';

const DashboardGlobal = () => {
  const [kpisData, setKpisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sector: 'All',
    fechaInicio: '2025-08-01',
    fechaFin: '2025-11-17',
    empresaId: null,
    localId: null,
    comparison: 'All',
    budget: false,
    category: ['All']
  });

  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🚀 Fetching KPIs con params:', {
        fechaInicio: filters.fechaInicio,
        fechaFin: filters.fechaFin,
        empresaId: filters.empresaId,
        localId: filters.localId
      });

      const response = await kpiService.getKPIsGlobales({
        fechaInicio: filters.fechaInicio,
        fechaFin: filters.fechaFin,
        empresaId: filters.empresaId,
        localId: filters.localId
      });
      
      if (response.success) {
        console.log('✅ KPIs cargados correctamente:', response.data);
        
        // El servicio ya devuelve la estructura correcta con data.kpis como objeto
        setKpisData(response.data);
        
        console.log('✅ kpisData.kpis establecido:', response.data.kpis);
        console.log('✅ Keys disponibles:', Object.keys(response.data.kpis || {}));
      } else {
        throw new Error('No se pudieron cargar los KPIs');
      }
    } catch (err) {
      console.error('❌ Error en fetch:', err);
      setError('Error al cargar los datos. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [filters.fechaInicio, filters.fechaFin, filters.empresaId, filters.localId]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  const handleRefresh = () => {
    fetchKPIs();
  };

  // 🔥 ACCESO DIRECTO Y SIMPLIFICADO A LOS KPIs
  // El objeto kpis ya está en formato camelCase gracias a la transformación en el servicio
  const kpis = kpisData?.kpis || {};
  
  // Logging para debug
  useEffect(() => {
    if (kpis && Object.keys(kpis).length > 0) {
      console.log('📊 KPIs disponibles en el componente:', Object.keys(kpis));
      console.log('📊 Ejemplo - numeroCompanias:', kpis.numeroCompanias);
      console.log('📊 Ejemplo - ingresosTotales:', kpis.ingresosTotales);
    }
  }, [kpis]);

  const handleApplyFilters = (appliedFilters) => {
    setFilters({
      sector: appliedFilters.sector || 'All',
      fechaInicio: appliedFilters.dateFrom || filters.fechaInicio,
      fechaFin: appliedFilters.dateTo || filters.fechaFin,
      empresaId: appliedFilters.company || null,
      localId: appliedFilters.local || null,
      comparison: appliedFilters.comparison || 'All',
      budget: appliedFilters.budget || false,
      category: appliedFilters.category || ['All']
    });
  };

  const handleClearFilters = () => {
    setFilters({
      sector: 'All',
      fechaInicio: '2025-08-01',
      fechaFin: '2025-11-17',
      empresaId: null,
      localId: null,
      comparison: 'All',
      budget: false,
      category: ['All']
    });
  };

  const hasActiveFilters = () => {
    return filters.sector !== 'All' ||
           filters.empresaId !== null ||
           filters.localId !== null ||
           filters.comparison !== 'All' ||
           filters.budget ||
           !filters.category.includes('All');
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.sector !== 'All') count++;
    if (filters.empresaId !== null) count++;
    if (filters.localId !== null) count++;
    if (filters.comparison !== 'All') count++;
    if (filters.budget) count++;
    if (!filters.category.includes('All')) count++;
    return count;
  };

  const handleRemoveFilter = (filterName) => {
    const newFilters = { ...filters };
    
    switch (filterName) {
      case 'sector':
        newFilters.sector = 'All';
        break;
      case 'empresaId':
        newFilters.empresaId = null;
        break;
      case 'localId':
        newFilters.localId = null;
        break;
      case 'comparison':
        newFilters.comparison = 'All';
        break;
      case 'budget':
        newFilters.budget = false;
        break;
      case 'category':
        newFilters.category = ['All'];
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
  };

  // 🎯 HELPER: Función para obtener valor de KPI de forma segura
  const getKPIValue = (kpiName, defaultValue = 0) => {
    return kpis[kpiName]?.valor ?? defaultValue;
  };

  const getKPIVariation = (kpiName) => {
    return kpis[kpiName]?.variacion;
  };

  const isKPIPositive = (kpiName) => {
    return kpis[kpiName]?.isPositive;
  };

  // 📊 CONFIGURACIÓN DE KPIs PARA LAS TARJETAS
  const kpiCardsRow1 = [
    { 
      key: 'numeroCompanias',
      title: "Nº Compañías", 
      subtitle: "Evol. de Empresas", 
      icon: "🏢", 
      bg: "#d1fae5", 
      formatter: formatNumber 
    },
    { 
      key: 'ingresosTotales',
      title: "Ingresos totales", 
      subtitle: "Evol. Ingresos totales", 
      icon: "💰", 
      bg: "#fef3c7", 
      formatter: formatCurrency 
    },
    { 
      key: 'totalInvertido',
      title: "Total invertido", 
      subtitle: "Evol. Inversión", 
      icon: "💼", 
      bg: "#dbeafe", 
      formatter: formatCurrency 
    },
    { 
      key: 'precioPromedioTicket',
      title: "Precio promedio por entrada/ticket", 
      subtitle: "Evol. Precio ticket", 
      icon: "🎫", 
      bg: "#fce7f3", 
      formatter: formatCurrency 
    },
    { 
      key: 'objetivoVsReal',
      title: "Objetivo VS real", 
      subtitle: "Evol. Objetivo vs real", 
      icon: "🎯", 
      bg: "#e0f2fe", 
      formatter: formatCurrency 
    }
  ];

  const kpiCardsRow2 = [
    { 
      key: 'numEmpleados',
      title: "Nº Empleados todas sociedades VIC", 
      subtitle: "Evol. de empleados", 
      icon: "👨‍💼", 
      bg: "#ede9fe", 
      formatter: formatNumber 
    },
    { 
      key: 'costesTotales',
      title: "Costes totales", 
      subtitle: "Evol. Costes operativos", 
      icon: "💸", 
      bg: "#fecaca", 
      formatter: formatCurrency 
    },
    { 
      key: 'totalActivo',
      title: "Total Activo", 
      subtitle: "Evol. Activo", 
      icon: "🏦", 
      bg: "#bfdbfe", 
      formatter: formatCurrency 
    },
    { 
      key: 'margenOperacion',
      title: "Margen de la operación", 
      subtitle: "Evol. Margen neto", 
      icon: "📊", 
      bg: "#dbeafe", 
      formatter: formatPercentage 
    },
    { 
      key: 'clientes',
      title: "Clientes", 
      subtitle: "Evol. Clientes", 
      icon: "👥", 
      bg: "#fed7aa", 
      formatter: formatNumber 
    }
  ];

  return (
    <>
      <TopHeader />
      
      <Container fluid className="px-4 py-4">        
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
                        
              <NavigationTabs />
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={handleRefresh} disabled={loading}>
                  <span className="material-symbols-outlined">
                    {loading ? 'hourglass_empty' : 'refresh'}
                  </span>
                </Button>
                <Button 
                  variant="outline-secondary" 
                  className="d-flex align-items-center gap-2 position-relative"
                  onClick={() => setShowFilters(true)}
                >
                  <span className="material-symbols-outlined">tune</span>
                  <span>Filtros</span>
                  {hasActiveFilters() && (
                    <Badge 
                      bg="primary" 
                      pill 
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: '0.65rem' }}
                    >
                      {countActiveFilters()}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {hasActiveFilters() && (
        <Row className="mb-3">
          <Col>
            <div 
              className="d-flex align-items-center flex-wrap gap-2 p-3 rounded border"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <span className="fw-semibold text-muted me-2">
                <span className="material-symbols-outlined" style={{ fontSize: '20px', verticalAlign: 'middle' }}>filter_alt</span>
                {' '}Filtros activos:
              </span>
              
              {filters.sector !== 'All' && (
                <Badge 
                  bg="warning" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('sector')}
                >
                  <span>Sector: {filters.sector}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              {filters.empresaId && (
                <Badge 
                  bg="primary" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('empresaId')}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>business</span>
                  <span>Empresa: {filters.empresaId}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              {filters.localId && (
                <Badge 
                  bg="success" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('localId')}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>store</span>
                  <span>Local: {filters.localId}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              {filters.comparison !== 'All' && (
                <Badge 
                  bg="secondary" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('comparison')}
                >
                  <span>Comparación: {filters.comparison}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              {filters.budget && (
                <Badge 
                  bg="dark" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('budget')}
                >
                  <span>Budget</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              {!filters.category.includes('All') && (
                <Badge 
                  bg="info" 
                  className="d-inline-flex align-items-center gap-2 py-2 px-3"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => handleRemoveFilter('category')}
                >
                  <span>Categorías: {filters.category.join(', ')}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                </Badge>
              )}
              
              <Button 
                variant="outline-danger" 
                size="sm" 
                className="d-inline-flex align-items-center gap-1 ms-auto"
                onClick={handleClearFilters}
                style={{ fontSize: '0.85rem' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
                Limpiar filtros
              </Button>
            </div>
          </Col>
        </Row>
        )}

        <FilterDashGlobal 
          show={showFilters}
          handleClose={() => setShowFilters(false)}
          onApplyFilters={handleApplyFilters}
        />

        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {/* KPIs Row 1 */}
        <Row className="g-3 mb-3">
          {kpiCardsRow1.map((config) => (
            <Col key={config.key} style={{ flex: '1 1 0', minWidth: '200px' }}>
              <KPICard
                title={config.title}
                value={loading ? '...' : config.formatter(getKPIValue(config.key))}
                subtitle={config.subtitle}
                icon={config.icon}
                iconBgColor={config.bg}
                variation={getKPIVariation(config.key)}
                isPositive={isKPIPositive(config.key)}
                loading={loading}
              />
            </Col>
          ))}
        </Row>

        {/* KPIs Row 2 */}
        <Row className="g-3 mb-3">
          {kpiCardsRow2.map((config) => (
            <Col key={config.key} style={{ flex: '1 1 0', minWidth: '200px' }}>
              <KPICard
                title={config.title}
                value={loading ? '...' : config.formatter(getKPIValue(config.key))}
                subtitle={config.subtitle}
                icon={config.icon}
                iconBgColor={config.bg}
                variation={getKPIVariation(config.key)}
                isPositive={isKPIPositive(config.key)}
                loading={loading}
              />
            </Col>
          ))}
        </Row>

        {/* VISITANTES */}
        <Row className="g-3 mb-4">
          <Col lg={6}>
            <VisitantesChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              localId={filters.localId}
            />
          </Col>
          <Col lg={6}>
            <BreakEvenChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              localId={filters.localId}
            />
          </Col>
        </Row>

        {/* FINANZAS - INGRESOS */}
        <Row className="g-3 mb-4">
          <Col lg={6}>
            <DistribucionIngresosChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              empresaId={filters.empresaId}
            />
          </Col>
          <Col lg={6}>
            <InvertidoVsEbitdaChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              empresaId={filters.empresaId}
            />
          </Col>
        </Row>

        {/* FINANZAS - APALANCAMIENTO */}
        <Row className="mb-4">
          <Col xs={12}>
            <h4 className="mb-3">FINANZAS - Apalancamiento</h4>
          </Col>
        </Row>
        <Row className="g-3 mb-4">
          <Col lg={4}>
            <DeudaPorAreaChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              empresaId={filters.empresaId}
            />
          </Col>
          <Col lg={4}>
            <ApalancamientoChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              empresaId={filters.empresaId}
            />
          </Col>
          <Col lg={4}>
            <ActivosPorAreaChart
              fechaInicio={filters.fechaInicio}
              fechaFin={filters.fechaFin}
              empresaId={filters.empresaId}
            />
          </Col>
        </Row>

        {/* FINANZAS - RETRASOS E IMPAGOS */}
        <Row className="mb-4">
          <Col xs={12}>
            <h4 className="mb-3">FINANZAS - Retrasos e Impagos</h4>
          </Col>
        </Row>
        <RetrasosImpagosCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />

        {/* OPERACIONES - COSTES */}
        <Row className="mb-4 mt-5">
          <Col xs={12}>
            <h4 className="mb-3">OPERACIONES - Ratios operativos - Costes</h4>
          </Col>
        </Row>
        <CostesCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />

        {/* OPERACIONES - MERCHANDISING */}
        <Row className="mb-4 mt-5">
          <Col xs={12}>
            <h4 className="mb-3">OPERACIONES - Ratios operativos - Merchandising</h4>
          </Col>
        </Row>
        <MerchandisingCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />

        {/* FOOD & BEVERAGE */}
        <Row className="mb-4 mt-5">
          <Col xs={12}>
            <h4 className="mb-3">Food & Beverage</h4>
          </Col>
        </Row>
        <FoodBeverageCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />

        {/* CRM Y FUNNEL DE VENTAS */}
        <Row className="mb-4 mt-5">
          <Col xs={12}>
            <h4 className="mb-3">CRM Y Funnel de ventas</h4>
          </Col>
        </Row>
        <CRMFunnelCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />

        {/* TICKETING */}
        <Row className="mb-4 mt-5">
          <Col xs={12}>
            <h4 className="mb-3">TICKETING</h4>
          </Col>
        </Row>
        <TicketingCharts
          fechaInicio={filters.fechaInicio}
          fechaFin={filters.fechaFin}
          empresaId={filters.empresaId}
        />
      </Container>
    </>
  );
};

export default DashboardGlobal;